import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import axios from "api";
import dayjs from "dayjs";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { userLogin } from "store/reducers/auth";
import { RootState } from "store";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const formatPrice = (amount: number) => {
  if (amount === 0) return "Free";
  return `₹${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const formatDuration = (days: number) => {
  if (days >= 365) return `${Math.round(days / 365)} year(s)`;
  if (days >= 30) return `${Math.round(days / 30)} month(s)`;
  return `${days} day(s)`;
};

interface BillingSubscriptionType {
  id: number;
  name: string;
  description: string | null;
  duration_days: number;
  price: string | number;
  is_active: boolean;
}

export default function VendorSubscriptionPage() {
  const { fetchSuccess, fetchError } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const client_id = user?.clientId ?? (user as { client_id?: number })?.client_id;
  const [subscriptionTypes, setSubscriptionTypes] = useState<
    BillingSubscriptionType[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedType, setSelectedType] = useState<BillingSubscriptionType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "" });

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await axios.get("/billing/subscription-types");
        setSubscriptionTypes(res.data.subscriptionTypes ?? []);
      } catch {
        setSubscriptionTypes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTypes();
  }, []);

  const handleSelectType = (st: BillingSubscriptionType) => {
    setSelectedType(st);
    const start = dayjs().startOf("day");
    const end = start.add(st.duration_days - 1, "day");
    setForm({
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
    });
    setOpenConfirm(true);
  };

  const updateEndFromStart = (startStr: string, durationDays: number) => {
    const end = dayjs(startStr).add(durationDays - 1, "day");
    setForm((f) => ({ ...f, end_date: end.format("YYYY-MM-DD") }));
  };

  const handleConfirm = async () => {
    if (!selectedType || !form.start_date || !form.end_date) return;
    if (dayjs(form.end_date).isBefore(form.start_date)) {
      fetchError("End date must be on or after start date");
      return;
    }
    setSubmitting(true);
    try {
      if (!client_id) {
        fetchError("Session error. Please sign in again.");
        setSubmitting(false);
        return;
      }
      await axios.post("/billing/agreements", {
        client_id,
        plan_name: selectedType.name,
        agreed_price: Number(selectedType.price),
        start_date: form.start_date,
        end_date: form.end_date,
      });
      fetchSuccess("Subscription created successfully.");
      setOpenConfirm(false);
      setSelectedType(null);
      const authRes = await axios.get("/auth");
      dispatch(userLogin({ user: authRes.data }));
      navigate("/dashboard");
    } catch (err: any) {
      fetchError(
        err.response?.data?.error || "Something went wrong."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = theme.palette.mode === "dark";
  const cardBg = isDark
    ? alpha(theme.palette.background.paper, 0.5)
    : alpha(theme.palette.grey[800], 0.08);
  const textOnDark = isDark ? theme.palette.text.primary : theme.palette.grey[900];
  const textMuted = isDark ? theme.palette.text.secondary : theme.palette.grey[600];

  return (
     <DashboardLayout>
       <DashboardNavbar />
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Choose a plan
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your free tier (5 customers) is complete. Select a subscription to continue adding customers and using all features.
      </Typography>

      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          background: isDark
            ? alpha(theme.palette.background.default, 0.6)
            : theme.palette.grey[100],
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            overflowX: "auto",
            pb: 1.5,
            minHeight: 260,
            alignItems: "stretch",
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 4,
              bgcolor: "action.hover",
            },
          }}
        >
          <Card
            elevation={0}
            sx={{
              flex: "0 0 280px",
              borderRadius: 3,
              background: cardBg,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              color: textOnDark,
              display: "flex",
              flexDirection: "column",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
            }}
          >
            <CardContent sx={{ py: 2.5, px: 2.5, flex: 1, display: "flex", flexDirection: "column", "&:last-child": { pb: 2.5 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupsOutlinedIcon sx={{ fontSize: 22, color: textMuted }} />
                <Typography variant="body2" fontWeight={500}>Up to 5 customers</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: textOnDark, mt: 1.5 }}>
                Free
              </Typography>
              <Typography variant="body2" sx={{ color: textMuted, mt: 0.5 }}>
                See what you can do
              </Typography>
              <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: textOnDark, mt: 2 }}>
                Free
              </Typography>
              <Typography variant="caption" sx={{ color: textMuted }}>/ month</Typography>
              <Box sx={{ flex: 1, minHeight: 16 }} />
              <Button
                fullWidth
                variant="outlined"
                disabled
                sx={{
                  mt: 2,
                  py: 1.25,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.action.disabled, 0.08),
                  borderColor: "divider",
                  color: textMuted,
                }}
              >
                Your current plan (maxed)
              </Button>
            </CardContent>
          </Card>

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", px: 4 }}>
              <Typography color="text.secondary">Loading plans...</Typography>
            </Box>
          ) : (
            subscriptionTypes.map((st, index) => {
              const isHighlighted = index === 0;
              return (
                <Card
                  key={st.id}
                  elevation={0}
                  sx={{
                    flex: "0 0 280px",
                    borderRadius: 3,
                    background: isHighlighted ? alpha(theme.palette.primary.main, 0.06) : cardBg,
                    border: `2px solid ${isHighlighted ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.3)}`,
                    color: textOnDark,
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: isHighlighted ? 8 : 4,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent sx={{ py: 2.5, px: 2.5, flex: 1, display: "flex", flexDirection: "column", "&:last-child": { pb: 2.5 } }}>
                    {isHighlighted && (
                      <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.primary.main, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Popular
                      </Typography>
                    )}
                    <Typography variant="h5" fontWeight={700} sx={{ color: textOnDark, mt: isHighlighted ? 0.5 : 1.5 }}>
                      {st.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: textMuted, minHeight: 40, mt: 0.5 }}>
                      {st.description || formatDuration(st.duration_days)}
                    </Typography>
                    <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: theme.palette.primary.main, mt: 1.5 }}>
                      {formatPrice(Number(st.price))}
                    </Typography>
                    <Typography variant="caption" sx={{ color: textMuted }}>
                      for {formatDuration(st.duration_days)}
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 16 }} />
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleSelectType(st)}
                      sx={{
                        mt: 2,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: isHighlighted ? 2 : 0,
                      }}
                    >
                      Upgrade to {st.name}
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      </Box>

      <Dialog
        open={openConfirm}
        onClose={() => !submitting && setOpenConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedType ? (
            <>
              {selectedType.name}{" "}
              <Typography
                component="span"
                color="primary.main"
                fontWeight={700}
                sx={{ ml: 1 }}
              >
                {formatPrice(Number(selectedType.price))}
              </Typography>
            </>
          ) : (
            "Confirm plan"
          )}
        </DialogTitle>
        <DialogContent>
          {selectedType && (
            <Grid container spacing={2.5} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1.25}>
                  <InputLabel>Start date</InputLabel>
                  <TextField
                    fullWidth
                    type="date"
                    value={form.start_date}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, start_date: v }));
                      updateEndFromStart(v, selectedType.duration_days);
                    }}
                    inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1.25}>
                  <InputLabel>End date</InputLabel>
                  <TextField
                    fullWidth
                    type="date"
                    value={form.end_date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, end_date: e.target.value }))
                    }
                    inputProps={{
                      min: form.start_date,
                      max: dayjs(form.start_date)
                        .add(selectedType.duration_days - 1, "day")
                        .format("YYYY-MM-DD"),
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setOpenConfirm(false)}
            color="secondary"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={
              submitting ||
              !selectedType ||
              !form.start_date ||
              !form.end_date ||
              dayjs(form.end_date).isBefore(form.start_date)
            }
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {submitting ? "Creating…" : "Confirm & subscribe"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
   </DashboardLayout>
  );
}
